 
import {
  ArrowBack,
  CheckCircle,
  Inventory,
  LocalShipping,
  QuestionMark,
  Schedule
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import GenericSearchControls from '../components/common/GenericSearchControls';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import zoeShipmentTrackingService from '../services/zoe-shipment-tracking-service';

export default function ZoeShipmentsByStatusPage() {
  const navigate = useNavigate();
  const { status } = useParams(); // Get status from URL params
  const [shipments, setShipments] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });

  // Status configuration for display and icons
  const getStatusConfig = (status) => {
    const configs = {
      Delivered: {
        icon: <CheckCircle />,
        color: 'success',
        bgColor: '#0D1F18',
        description: 'Successfully delivered shipments'
      },
      'In Transit': {
        icon: <LocalShipping />,
        color: 'info',
        bgColor: '#121B2F',
        description: 'Shipments currently in transit'
      },
      'Checked In': {
        icon: <Inventory />,
        color: 'primary',
        bgColor: '#162232',
        description: 'Shipments checked into facility'
      },
      'Checked Out': {
        icon: <LocalShipping />,
        color: 'secondary',
        bgColor: '#1E2235',
        description: 'Shipments checked out for delivery'
      },
      Pending: {
        icon: <Schedule />,
        color: 'warning',
        bgColor: '#1F1A10',
        description: 'Shipments awaiting processing'
      },
      Unclassified: {
        icon: <QuestionMark />,
        color: 'error',
        bgColor: '#1F1010',
        description: 'Shipments with unknown status'
      }
    };
    return (
      configs[status] || {
        icon: <LocalShipping />,
        color: 'default',
        bgColor: '#1A1D27',
        description: `Shipments with status: ${status}`
      }
    );
  };

  const statusConfig = getStatusConfig(status);

  const fetchShipmentsByStatus = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await zoeShipmentTrackingService.getShipmentsByStatus(
          status,
          page,
          limit
        );
        if (response.success) {
          setShipments(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setShipments([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to fetch shipments by status:', err);
      }
      setLoading(false);
    },
    [status, pagination.limit]
  );

  useEffect(() => {
    fetchShipmentsByStatus();
  }, [fetchShipmentsByStatus]);

  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const filterObj = {};
        filters.forEach((f) => {
          if (f.field && f.value) filterObj[f.field] = f.value;
        });
        if (dateFilter !== 'default') {
          filterObj.date_filter = dateFilter;
        }
        if (searchValue) {
          filterObj.TrackingNumber = searchValue;
        }

        const response = await zoeShipmentTrackingService.getShipmentsByStatus(
          status,
          page,
          limit,
          filterObj,
          sortBy,
          sortOrder
        );

        if (response.success) {
          setShipments(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setShipments([]);
          console.error(response.message);
        }
      } catch (err) {
        console.error('Failed to search shipments:', err);
      }
      setLoading(false);
    },
    // sortBy/sortOrder are now sent to the service, so they must be deps or
    // handleSearch closes over stale values.
    [status, searchValue, filters, dateFilter, pagination.limit, sortBy, sortOrder]
  );

  const debouncedSearch = useMemo(
    () => debounce((page, limit) => handleSearch(page, limit), 500),
    [handleSearch]
  );

  // Shared with PaginatedControls so paging uses the same search-vs-fetchAll
  // decision as this effect.
  const hasSearchCriteria =
    !!searchValue ||
    filters.length > 0 ||
    dateFilter !== 'default' ||
    sortBy !== 'default' ||
    sortOrder !== 'default';

  useEffect(() => {
    // Reset to page 1 unconditionally, then either search or fall back to the
    // unfiltered list. The `else` is the fix: with no branch here, clearing
    // the last filter left the previous filtered results frozen on screen
    // while the search UI showed empty — it looked like clearing did nothing.
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (hasSearchCriteria) {
      debouncedSearch(1, pagination.limit);
    } else {
      // Cancel any in-flight debounced search so a slower stale response
      // cannot land afterwards and re-apply the filter just cleared.
      debouncedSearch.cancel();
      fetchShipmentsByStatus(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch,
    hasSearchCriteria,
    fetchShipmentsByStatus
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'TrackingNumber':
        return 'Tracking Number';
      case 'ServiceName':
        return 'Service Name';
      case 'CarrierName':
        return 'Carrier Name';
      case 'OrderNumber':
        return 'Order Number';
      case 'Source':
        return 'Source';
      case 'Username':
        return 'Username';
      default:
        return field;
    }
  };

  const filterOptions = [
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'ServiceName', label: 'Service Name' },
    { value: 'CarrierName', label: 'Carrier Name' },
    { value: 'OrderNumber', label: 'Order Number' },
    { value: 'Source', label: 'Source' },
    { value: 'Username', label: 'Username' }
  ];

  const sortByList = [
    { value: 'ZoeShipmentTrackingId', label: 'Shipment ID' },
    { value: 'TrackingNumber', label: 'Tracking Number' },
    { value: 'ServiceName', label: 'Service Name' },
    { value: 'CarrierName', label: 'Carrier Name' },
    { value: 'OrderNumber', label: 'Order Number' },
    { value: 'CreatedDate', label: 'Created Date' },
    { value: 'LastUpdated', label: 'Last Updated' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        {/* Header with Breadcrumbs */}
        <Box mb={3}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link
              component={RouterLink}
              to="/zoe-shipment-tracking"
              color="inherit"
            >
              Zoe Shipment Tracking
            </Link>
            <Typography color="text.primary">{status} Shipments</Typography>
          </Breadcrumbs>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{ mb: 1 }}
            >
              Back to Dashboard
            </Button>
          </Box>

          <Paper sx={{ p: 3, mb: 3, backgroundColor: statusConfig.bgColor }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ color: `${statusConfig.color}.main`, fontSize: 48 }}>
                {statusConfig.icon}
              </Box>
              <Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  {status} Shipments
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ mb: 2 }}
                >
                  {statusConfig.description}
                </Typography>
                <Chip
                  label={`Status: ${status}`}
                  color={statusConfig.color}
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
          </Paper>
        </Box>

        <div style={{ margin: '20px auto', width: '90%' }}>
          <GenericSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            searchFieldLabel="Tracking Number"
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            enableDateFilter={true}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
            resetAll={() => {
              setSearchValue('');
              setFilters([]);
              setDateFilter('default');
              setSortBy('default');
              setSortOrder('default');
              setPagination({ currentPage: 1, totalPages: 1, limit: 20 });
              setShowAdvancedSearch(false);
              setFilterDraft({ field: '', value: '' });
              localStorage.removeItem('searchValue');
              localStorage.removeItem('advancedSearchFilters');
              fetchShipmentsByStatus();
            }}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={shipments.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchShipmentsByStatus}
            isSearchActive={hasSearchCriteria}
          />

          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6">Loading shipments...</Typography>
            </Paper>
          ) : shipments.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ color: 'text.secondary', fontSize: 48, mb: 2 }}>
                {statusConfig.icon}
              </Box>
              <Typography variant="h6" gutterBottom>
                No {status.toLowerCase()} shipments found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchValue || filters.length > 0 || dateFilter !== 'default'
                  ? 'Try adjusting your search filters.'
                  : `There are currently no shipments with status "${status}".`}
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Tracking Number</TableCell>
                    <TableCell>Carrier</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Order Number</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shipments.map((shipment) => (
                    <TableRow key={shipment.ZoeShipmentTrackingId}>
                      <TableCell>{shipment.ZoeShipmentTrackingId}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {shipment.TrackingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{shipment.CarrierName || 'N/A'}</TableCell>
                      <TableCell>{shipment.ServiceName || 'N/A'}</TableCell>
                      <TableCell>{shipment.OrderNumber || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(shipment.CreatedDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(shipment.LastUpdated)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/zoe-shipment-tracking/${shipment.ZoeShipmentTrackingId}`
                            )
                          }
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={shipments.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchShipmentsByStatus}
            isSearchActive={hasSearchCriteria}
          />
        </div>
      </div>
    </div>
  );
}
